import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BulkDto, DetailViewDto } from '@myarvato/modules/transport/models';
import { BulkColumn } from './models/bulk-column.model';
import { TreeNode } from 'primeng/api';
import { TreeTableModule } from 'primeng/treetable';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { HighlightPipe } from '@myarvato/shared/pipelines';

// Preserves the real API id alongside the synthetic display label assigned by toTreeNode
type BulkNodeData = BulkDto & { readonly realId?: string };

@Component({
  selector: 'transport-details-bulk',
  imports: [TranslatePipe, TreeTableModule, InputTextModule, IconFieldModule, InputIconModule, HighlightPipe],
  templateUrl: './details-bulk.component.html',
  styleUrl: './details-bulk.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsBulkComponent {
  private static readonly TYPE_PALLET = 'pallet';
  private static readonly TYPE_PARCEL = 'parcel';

  private readonly translateService = inject(TranslateService);

  // Inputs
  public readonly details = input.required<DetailViewDto>();

  protected readonly searchTerm = signal('');

  protected readonly bulkHandlingUnits = computed<TreeNode<BulkNodeData>[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();

    if (!term) {
      return this.allNodes();
    }
    // Root nodes are always kept; only their descendants are pruned by the search term
    return this.allNodes().map((node) => this.filterNode(node, term).node);
  });

  // Columns for the bulk handling unit table
  protected readonly cols: BulkColumn[] = [
    { field: 'id', style: 'width: 25%', header: this.translateService.instant('pages.transport.detail-page.bulk.columns.id') },
    {
      field: 'arvatoReference',
      style: 'width: 25%',
      header: this.translateService.instant('pages.transport.detail-page.bulk.columns.arvatoReference'),
    },
    { field: 'carrier', style: 'width: 10%', header: this.translateService.instant('pages.transport.detail-page.bulk.columns.carrier') },
    {
      field: 'trackingNumber',
      style: 'width: 20%',
      header: this.translateService.instant('pages.transport.detail-page.bulk.columns.trackingNumber'),
    },
    { field: 'progress', header: this.translateService.instant('pages.transport.detail-page.bulk.columns.progress') },
  ];

  // Bulk master row is derived from DetailViewDto; individual bulks are the flat API array
  private readonly allNodes = computed<TreeNode<BulkNodeData>[]>(() => {
    const details = this.details();
    const bulks = details.bulk;

    if (!bulks?.length) {
      return [];
    }

    const bulkMaster: BulkDto = {
      id: details.bulkMasterNumber,
      type: 'master',
      arvatoReference: details.arvatoDeliveryNumber,
      carrier: details.carrierDescription,
      trackingNumber: details.trackingNumber,
      progress: details.progress,
    };

    // Group flat items: every Parcel that follows a Pallet in the array becomes a child of that Pallet
    const grouped = this.groupFlatBulks(bulks);

    // Create TreeNode children for each grouped BulkDto, assigning synthetic IDs for display
    const bulkChildren = grouped.map((child, index) => {
      const seqNumber = String(index + 1).padStart(3, '0');
      const typeLabel = child.type.toUpperCase();
      return this.toTreeNode(child, false, `${typeLabel}-${seqNumber}`);
    });

    return [{ data: bulkMaster, expanded: false, children: bulkChildren }];
  });

  // Converts a flat Pallet/Parcel list into a tree: each Parcel runs until the next Pallet
  private groupFlatBulks(bulks: ReadonlyArray<BulkDto>): BulkDto[] {
    const groups: BulkDto[][] = [];
    let openPalletGroup: BulkDto[] | null = null;

    // A pallet opens a new group, its following parcels join that group,
    // anything else (an orphan Parcel or another type) starts its own standalone group
    for (const item of bulks) {
      const type = item.type.toLowerCase();

      if (type === DetailsBulkComponent.TYPE_PALLET) {
        openPalletGroup = [item];
        groups.push(openPalletGroup);
        continue;
      }

      if (type === DetailsBulkComponent.TYPE_PARCEL && openPalletGroup) {
        openPalletGroup.push(item);
        continue;
      }

      openPalletGroup = null;
      groups.push([item]);
    }

    // The first entry of each group is the parent; any remaining entries become its children
    return groups.map(([parent, ...children]) => (children.length ? { ...parent, children } : parent));
  }

  // Recursively converts a BulkDto into a TreeNode, assigning synthetic IDs for display
  private toTreeNode(hu: BulkDto, expanded: boolean, syntheticId?: string): TreeNode<BulkNodeData> {
    return {
      data: syntheticId ? { ...hu, id: syntheticId, realId: hu.id } : hu,
      expanded,
      children: (hu.children ?? []).map((child, index) => {
        const runningNumber = String(index + 1).padStart(3, '0');
        const typeLabel = child.type.toUpperCase();
        return this.toTreeNode(child, expanded, `${typeLabel}-${runningNumber}`);
      }),
    };
  }

  // Recursively checks if a handling unit matches the search term
  private matchesSearch(hu: BulkNodeData, term: string): boolean {
    return (
      (hu.realId ?? hu.id).toLowerCase().includes(term) ||
      hu.arvatoReference.toLowerCase().includes(term) ||
      hu.carrier.toLowerCase().includes(term) ||
      hu.trackingNumber.toLowerCase().includes(term) ||
      hu.progress.toLowerCase().includes(term)
    );
  }

  // Single-pass prune: filters non-matching branches while reporting whether this node's subtree matched
  private filterNode(node: TreeNode<BulkNodeData>, term: string): { node: TreeNode<BulkNodeData>; matches: boolean } {
    const filteredChildren = (node.children ?? [])
      .map((child) => this.filterNode(child, term))
      .filter((result) => result.matches)
      .map((result) => result.node);

    const selfMatches = !!node.data && this.matchesSearch(node.data, term);
    return {
      node: { ...node, expanded: filteredChildren.length > 0, children: filteredChildren },
      matches: selfMatches || filteredChildren.length > 0,
    };
  }
}
